/**
 * Photosensitivity gate — the Horizon port of lumenate.co's photo-sensitivity warning modal.
 *
 * Live (WordPress) does this with a Spectra popup block plus a footer snippet that adds a
 * capture-phase click listener on `.single_add_to_cart_button`, cancels the Woo add, opens the
 * popup, and re-clicks the saved button once the customer acknowledges. This mirrors that
 * exactly against Horizon's own add-to-cart button, which carries `name="add"` everywhere it
 * is rendered — the PDP buybox, the quick-add drawer, and the sticky bar (which "puppets" the
 * real button with `.click()`, so intercepting the button covers the bar for free).
 *
 * Capture phase is what makes it work: Horizon's `on:click` bindings are delegated from a
 * bubble-phase listener on `document` (assets/component.js), so stopping the event during
 * capture keeps both the add-to-cart animation and the implicit form submit from running.
 */

const ADD_TO_CART_BUTTON = 'button[name="add"]';
// Opt-out for add buttons that must not be gated. Live only hooks the PDP's
// `.single_add_to_cart_button`, so FunnelKit's in-cart upsell "Add" adds straight to the basket
// with no warning — the customer already cleared the gate to have a cart, and the upsell is the
// app subscription, not the light mask. Matching `button[name="add"]` catches that button too,
// hence the escape hatch rather than a narrower selector (the broad match is what covers the
// sticky bar and quick-add for free).
const GATE_SKIP = '[data-photosensitivity-skip]';
const STORAGE_KEY = 'lumenate:photosensitivity-acknowledged';

class PhotosensitivityGate extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #pendingButton = null;

  #acknowledged = false;

  connectedCallback() {
    this.#acknowledged = this.#storage?.getItem(STORAGE_KEY) === '1';

    document.addEventListener('click', this.#interceptAddToCart, { capture: true });
    this.addEventListener('click', this.#handleModalClick);
    this.#dialog?.addEventListener('dialog:close', this.#reset);
    this.#externalizeLinks();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#interceptAddToCart, { capture: true });
    this.#dialog?.removeEventListener('dialog:close', this.#reset);
  }

  /**
   * Where the acknowledgement is remembered, per the section's setting. Live remembers nothing —
   * its cookie code is commented out — so `never` is the default and matches it.
   *
   * @returns {Storage | null}
   */
  get #storage() {
    try {
      if (this.dataset.remember === 'session') return window.sessionStorage;
      if (this.dataset.remember === 'always') return window.localStorage;
    } catch {
      // Storage access throws in some privacy modes; fall through to not remembering.
    }
    return null;
  }

  /** @returns {(HTMLElement & { showDialog(): void, closeDialog(): Promise<void> }) | null} */
  get #dialog() {
    return /** @type {any} */ (this.querySelector('dialog-component'));
  }

  /** @param {MouseEvent} event */
  #interceptAddToCart = (event) => {
    if (this.#acknowledged) return;

    const target = event.target instanceof Element ? event.target : null;
    const button = /** @type {HTMLButtonElement | null} */ (target?.closest(ADD_TO_CART_BUTTON));
    if (!button || button.disabled || button.closest(GATE_SKIP)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.#pendingButton = button;
    this.#open();
  };

  async #open() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // dialog.js is loaded with a low fetch priority, so the element may not be upgraded yet on
    // an early click.
    await customElements.whenDefined('dialog-component');
    dialog.showDialog();
  }

  // QA (LUM-65): links in the disclosure copy must open in a new tab. The copy is merchant
  // richtext, whose editor can't set a link target, so every link in the dialog is externalised
  // here rather than baked into one translation string — covers both steps and any link an admin
  // adds later.
  #externalizeLinks() {
    for (const link of this.querySelectorAll('a[href]')) {
      link.target = '_blank';
      link.rel = 'noopener';
    }
  }

  /** @param {MouseEvent} event */
  #handleModalClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-photosensitivity-continue]')) this.#showStep('2');
    if (target.closest('[data-photosensitivity-acknowledge]')) this.#acknowledge();
  };

  /** @param {string} step */
  #showStep(step) {
    for (const pane of this.querySelectorAll('[data-photosensitivity-step]')) {
      /** @type {HTMLElement} */ (pane).hidden = pane.getAttribute('data-photosensitivity-step') !== step;
    }

    // Live scrolls the window to the top when the checklist replaces the warning; here the
    // scroller is the dialog itself.
    this.querySelector('dialog')?.scrollTo({ top: 0 });
  }

  async #acknowledge() {
    this.#acknowledged = true;
    try {
      this.#storage?.setItem(STORAGE_KEY, '1');
    } catch {
      // Ignore write failures — the gate still opens once per page load.
    }

    // Read the pending button before closing: closing fires `dialog:close`, which clears it.
    const button = this.#pendingButton;
    this.#pendingButton = null;

    await this.#dialog?.closeDialog();
    button?.click();
  }

  // Dismissing without acknowledging cancels the add, as it does on live, and puts the modal
  // back on step one for the next attempt.
  #reset = () => {
    this.#pendingButton = null;
    this.#showStep('1');
  };
}

if (!customElements.get('photosensitivity-gate')) {
  customElements.define('photosensitivity-gate', PhotosensitivityGate);
}
